import Link from "next/link";
import { colorSwatchFor, isColorAttribute } from "@/lib/catalog/colorSwatches";
import type { ProductFamilyItem, ProductFamilyResponse } from "@/types/productFamily";
import { FamilyOptionsScroller } from "./FamilyOptionsScroller";

const attributeOptionCollator = new Intl.Collator("pt-BR", {
  numeric: true,
  sensitivity: "base",
});

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

interface OptionView {
  item: ProductFamilyItem;
  label: string;
  color: string | null;
}

// Cor: bolinha (amostra) com o nome no título e no leitor de tela; a atual
// ganha um anel azul. Indisponível: traço diagonal. Cor sem amostra
// conhecida e as outras características (corrente, bitola...): pílula com
// texto. background inline porque a cor vem do nome (valor dinâmico).
function ColorSwatch({ option }: { option: OptionView }) {
  const { item, label, color } = option;
  const unavailable = !item.inStock;
  const name = `${label}${unavailable ? " — fora de estoque" : ""}`;
  const circle = (
    <span
      aria-hidden="true"
      className="relative block h-8 w-8 overflow-hidden rounded-full border border-black/10 shadow-[inset_0_1px_2px_rgba(0,0,0,0.12)]"
      style={{ background: color ?? undefined }}
    >
      {unavailable ? (
        <span className="absolute left-1/2 top-[-20%] h-[140%] w-0.5 -translate-x-1/2 rotate-45 bg-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.15)]" />
      ) : null}
    </span>
  );
  const base = `inline-flex shrink-0 snap-start items-center justify-center rounded-full p-1 transition ${focusRing}`;

  if (item.isCurrent) {
    return (
      <span aria-current="page" title={name} className={`${base} ring-2 ring-primary`}>
        {circle}
        <span className="sr-only">{name} (selecionado)</span>
      </span>
    );
  }
  return (
    <Link
      href={item.href}
      prefetch={false}
      title={name}
      aria-label={name}
      className={`${base} ring-2 ring-transparent hover:ring-secondary ${unavailable ? "opacity-50" : ""}`}
    >
      {circle}
    </Link>
  );
}

function TextOption({ option }: { option: OptionView }) {
  const { item, label } = option;
  const unavailable = !item.inStock;
  const base = `inline-flex min-h-9 min-w-11 shrink-0 snap-start items-center justify-center whitespace-nowrap rounded-full border px-4 text-sm font-medium leading-none transition-colors ${focusRing}`;

  if (item.isCurrent) {
    return (
      <span aria-current="page" className={`${base} border-primary bg-primary/5 font-semibold text-primary`}>
        {label}
      </span>
    );
  }
  return (
    <Link
      href={item.href}
      prefetch={false}
      aria-label={`${label}${unavailable ? " — fora de estoque" : ""}`}
      className={`${base} border-slate-200 bg-white text-foreground hover:border-secondary hover:text-secondary ${
        unavailable ? "text-muted line-through decoration-slate-400 opacity-60" : ""
      }`}
    >
      {label}
    </Link>
  );
}

export function ProductFamilyOptions({ data }: { data: ProductFamilyResponse }) {
  const attribute = data.family.attributes[0];
  if (!attribute) return null;

  const items = data.items
    .filter((item) => item.attributes[attribute.taxonomy])
    .sort((first, second) => {
      const firstLabel = first.attributes[attribute.taxonomy].label;
      const secondLabel = second.attributes[attribute.taxonomy].label;

      return (
        attributeOptionCollator.compare(firstLabel, secondLabel) ||
        first.productId - second.productId
      );
    });
  if (items.length < 2) return null;

  const showColors = isColorAttribute(attribute);
  const options: OptionView[] = items.map((item) => {
    const label = item.attributes[attribute.taxonomy].label;
    return { item, label, color: showColors ? colorSwatchFor(label) : null };
  });
  // Amostras só quando todas as cores são conhecidas; senão pílulas para todas
  // (misturar bolinha e texto na mesma linha fica confuso).
  const swatches = showColors && options.every((option) => option.color);
  const currentLabel = options.find((option) => option.item.isCurrent)?.label;

  return (
    <section className="mb-4 min-w-0" aria-labelledby="product-family-label">
      <h2 id="product-family-label" className="mb-2 text-sm text-muted">
        <span className="font-semibold text-foreground">{attribute.label}:</span>{" "}
        {currentLabel ? <span className="font-medium text-foreground">{currentLabel}</span> : null}
      </h2>
      <FamilyOptionsScroller label={attribute.label}>
        {options.map((option) =>
          swatches ? (
            <ColorSwatch key={option.item.productId} option={option} />
          ) : (
            <TextOption key={option.item.productId} option={option} />
          ),
        )}
      </FamilyOptionsScroller>
    </section>
  );
}
